import { useApiMutation } from "@gladis/api-client";
import type { PropertyDetailDto } from "@gladis/shared";
import { Button, Label, SelectField, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { PROPERTIES_KEY } from "./PropertyList.js";
import { propertyDetailKey } from "./ValuationHistory.js";

/**
 * Corrects what a property IS. Never what it is worth, and never what was paid for it.
 *
 * ==========================================================================================
 * A SEPARATE FORM FROM `PropertyForm`, DELIBERATELY
 * ==========================================================================================
 *
 * Creating a property establishes three things an edit may not touch: the purchase price, the
 * purchase date, and the opening valuation. They are not fields that happen to be read-only
 * afterwards — `UpdatePropertyRequestSchema` has no slot for any of them, and `PropertyEdit` in the
 * domain names only the descriptive fields.
 *
 * Adding a `mode` to `PropertyForm` would have meant a form where a third of the inputs vanish and
 * the submit handler branches, which is two forms wearing one name. The shared parts are Zod's and
 * the domain's, where sharing is worth something.
 *
 * A CORRECTION IS NOT A CORRECTION OF HISTORY. Changing the address does not change what the
 * property was worth last year; the valuation history is untouched and stays append-only.
 *
 * ==========================================================================================
 * EDITING THE ADDRESS RE-RESOLVES THE PRICE-INDEX REGION
 * ==========================================================================================
 *
 * The server does that on every edit, not only when the address changed (FR-020). It is why this
 * form exists at all beyond tidiness: until it did, a property whose region had never resolved had
 * NO route back — the region is only set on create or address edit, and there was no way to edit.
 * The quarterly job now self-heals as well, but a member who corrects a mistyped postcode should
 * see the estimate start working, not wait a quarter.
 */
export interface PropertyEditFormProps {
  propertyId: string;
  property: PropertyDetailDto["property"];
  onSaved?: () => void;
}

export function PropertyEditForm({
  propertyId,
  property,
  onSaved,
}: PropertyEditFormProps): ReactNode {
  const t = useT();
  const [street, setStreet] = useState(property.address.street);
  const [houseNumber, setHouseNumber] = useState(property.address.houseNumber);
  const [houseAddition, setHouseAddition] = useState(property.address.houseAddition ?? "");
  const [postcode, setPostcode] = useState(property.address.postcode);
  const [city, setCity] = useState(property.address.city);
  const [countryCode, setCountryCode] = useState(property.address.countryCode);
  const [propertyType, setPropertyType] = useState(property.propertyType);
  const [buildYear, setBuildYear] = useState(String(property.buildYear));
  const [inner, setInner] = useState(
    property.innerSurfaceAreaM2 === null ? "" : String(property.innerSurfaceAreaM2),
  );
  const [outer, setOuter] = useState(
    property.outerSurfaceAreaM2 === null ? "" : String(property.outerSurfaceAreaM2),
  );
  const [energyLabel, setEnergyLabel] = useState(property.energyLabel ?? "");

  const save = useApiMutation<void, Record<string, unknown>>(
    (client, input) => client.patch<void>(`/properties/${propertyId}`, input),
    /*
     * BOTH KEYS. An address change alters the row on the list page and the household's totals are
     * recomputed against the same payload, so refreshing only the detail view would leave the list
     * showing the old street one click away.
     */
    { invalidates: [propertyDetailKey(propertyId), PROPERTIES_KEY] },
  );

  // Required for NL only, matching the domain rule. The server refuses again with
  // `PROP_ENERGY_LABEL_REQUIRED`; this is the affordance, never the enforcement.
  const needsEnergyLabel = countryCode.trim().toUpperCase() === "NL";
  const canSubmit =
    street.trim() !== "" &&
    houseNumber.trim() !== "" &&
    postcode.trim() !== "" &&
    city.trim() !== "" &&
    buildYear.trim() !== "" &&
    (!needsEnergyLabel || energyLabel !== "");

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!canSubmit) return;

    save.mutate(
      {
        address: {
          street: street.trim(),
          houseNumber: houseNumber.trim(),
          houseAddition: houseAddition.trim() === "" ? null : houseAddition.trim(),
          postcode: postcode.trim(),
          city: city.trim(),
          countryCode: countryCode.trim().toUpperCase(),
        },
        propertyType,
        buildYear: Number(buildYear),
        innerSurfaceAreaM2: inner.trim() === "" ? null : Number(inner),
        outerSurfaceAreaM2: outer.trim() === "" ? null : Number(outer),
        energyLabel: energyLabel === "" ? null : energyLabel,
      },
      { onSuccess: () => onSaved?.() },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="edit-street">{t("prop.form.street")}</Label>
          <TextField
            id="edit-street"
            value={street}
            onChange={(event) => setStreet(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="edit-house-number">{t("prop.form.houseNumber")}</Label>
          <TextField
            id="edit-house-number"
            value={houseNumber}
            onChange={(event) => setHouseNumber(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="edit-house-addition">{t("prop.form.addition")}</Label>
          <TextField
            id="edit-house-addition"
            value={houseAddition}
            onChange={(event) => setHouseAddition(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="edit-postcode">{t("prop.form.postcode")}</Label>
          <TextField
            id="edit-postcode"
            value={postcode}
            onChange={(event) => setPostcode(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="edit-city">{t("prop.form.city")}</Label>
          <TextField
            id="edit-city"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="edit-country">{t("prop.form.country")}</Label>
          <TextField
            id="edit-country"
            value={countryCode}
            onChange={(event) => setCountryCode(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="edit-type">{t("prop.facts.type")}</Label>
          <SelectField
            id="edit-type"
            value={propertyType}
            onChange={(event) => setPropertyType(event.target.value as "house" | "apartment")}
          >
            <option value="house">{t("prop.facts.house")}</option>
            <option value="apartment">{t("prop.facts.apartment")}</option>
          </SelectField>
        </div>

        <div>
          <Label htmlFor="edit-build-year">{t("prop.form.buildYear")}</Label>
          <TextField
            id="edit-build-year"
            type="number"
            value={buildYear}
            onChange={(event) => setBuildYear(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="edit-inner">{t("prop.form.floorAreaEdit")}</Label>
          <TextField
            id="edit-inner"
            type="number"
            value={inner}
            onChange={(event) => setInner(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="edit-outer">{t("prop.form.plotEdit")}</Label>
          <TextField
            id="edit-outer"
            type="number"
            value={outer}
            onChange={(event) => setOuter(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="edit-energy-label">{t("prop.facts.energyLabel")}</Label>
          <SelectField
            id="edit-energy-label"
            value={energyLabel}
            onChange={(event) => setEnergyLabel(event.target.value)}
            required={needsEnergyLabel}
          >
            <option value="">
              {needsEnergyLabel ? t("prop.form.chooseOne") : t("prop.facts.notRecorded")}
            </option>
            {["A++", "A+", "A", "B", "C", "D", "E", "F", "G"].map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      {save.isError && <p className="text-danger text-sm">{errorSentence(save.error)}</p>}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!canSubmit || save.isPending}>
          {save.isPending ? t("common.saving") : t("exp.form.saveChanges")}
        </Button>
      </div>
    </form>
  );
}
